/**
 * Dialog zaawansowanych filtrów WiseTable – komponent prezentacyjny.
 * Logika w useWiseTableAdvancedFilters (WiseTable/hooks).
 */

import React, { FC, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Button as ButtonMui,
  Tabs,
  Tab,
  Box,
  Typography,
  Switch,
  List,
  ListItem,
  TextField,
  Divider
} from '@mui/material';
import { X, Trash, Plus, GripVertical } from 'react-bootstrap-icons';
import WiseSelect from 'components/wise/WiseSelect';
import FilterValueCell from 'components/wise/WiseTable/components/FilterValueCell';
import {
  IWiseTableColumn,
  IWiseTableAdvancedFilter,
  TWiseTableAdvancedFilterOperator
} from 'components/wise/types';
import { ADVANCED_FILTER_OPERATOR_LABELS } from 'components/wise/WiseTable/constants';
import type {
  IUseWiseTableAdvancedFiltersReturn,
  ISavedSettingsOptionItem
} from 'components/wise/WiseTable/hooks/useWiseTableAdvancedFilters';

import styles from 'theme/components/wise/WiseTable/components/AdvancedFiltersDialog/AdvancedFiltersDialog.module.scss';

export interface IAdvancedFiltersDialogProps extends IUseWiseTableAdvancedFiltersReturn {
  open: boolean;
  onClose: () => void;
  columns: IWiseTableColumn[];
  columnVisibility: Record<string, boolean>;
  pendingSelectedPresetId: string | null;
}

const AdvancedFiltersDialog: FC<IAdvancedFiltersDialogProps> = ({
  open,
  onClose,
  columns,
  pendingColumnVisibility,
  applyColumnVisibility,
  hasPendingColumnVisibilityChange,
  tabIndex,
  setTabIndex,
  filterRuleGroups,
  pendingSelectedPresetId,
  saveNameDialogType,
  saveNameValue,
  setSaveNameValue,
  overwritePresetWhenSaving,
  setOverwritePresetWhenSaving,
  columnOptions,
  operatorOptions,
  connectorOptions,
  getFilterByFieldSymbol,
  addRule,
  addConditionInGroup,
  removeRow,
  updateRow,
  handleOpenSaveFiltersNameDialog,
  handleOpenSaveSettingsNameDialog,
  handleCloseSaveNameDialog,
  handleConfirmSaveName,
  applyFiltersToRedux,
  clearAllFilters,
  hasAnyCompleteRow,
  handleColumnVisibilityToggle,
  pendingColumnOrder,
  setPendingColumnOrder,
  savedFilters,
  savedSettingsOptionsForSelect,
  handleSelectSettingsPreset,
  handleClearSavedFilters,
  handleApplySettingsPreset,
  getColumnLabel
}) => {
  const { t } = useTranslation();

  const orderedColumnsForVisibility = useMemo(() => {
    const order =
      pendingColumnOrder.length > 0 ? pendingColumnOrder : columns.map((c) => c.field_symbol);
    const bySymbol = new Map(columns.map((c) => [c.field_symbol, c]));
    const result: IWiseTableColumn[] = [];
    const added = new Set<string>();
    for (const id of order) {
      const col = bySymbol.get(id);
      if (col && !added.has(id)) {
        result.push(col);
        added.add(id);
      }
    }
    for (const col of columns) {
      if (!added.has(col.field_symbol)) result.push(col);
    }
    return result;
  }, [columns, pendingColumnOrder]);

  const handleColumnDragStart = useCallback(
    (e: React.DragEvent, fieldSymbol: string, index: number) => {
      e.dataTransfer.setData('application/json', JSON.stringify({ fieldSymbol, index }));
      e.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const handleColumnDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleColumnDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      try {
        const raw = e.dataTransfer.getData('application/json');
        const { index: dragIndex } = JSON.parse(raw) as { fieldSymbol: string; index: number };
        if (dragIndex === dropIndex) return;
        setPendingColumnOrder((prev) => {
          const order = prev.length > 0 ? [...prev] : columns.map((c) => c.field_symbol);
          const [removed] = order.splice(dragIndex, 1);
          const insertIndex = dropIndex > dragIndex ? dropIndex - 1 : dropIndex;
          order.splice(insertIndex, 0, removed);
          return order;
        });
      } catch {
        // ignore invalid drop
      }
    },
    [columns, setPendingColumnOrder]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: 400,
            maxHeight: 600,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}>
        <div className={styles.tabsWrapper}>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            className={styles.tabs}
            color="secondary"
            textColor="secondary"
            indicatorColor="secondary"
            sx={{
              width: '100%',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 0,
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              '& .MuiButtonBase-root.MuiTab-root': {
                backgroundColor: '#ffffff',
                textTransform: 'none'
              },
              '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
                fontWeight: 600,
                backgroundColor: '#f3f4f7'
              }
            }}>
            <Tab value={0} label={t('Zaawansowane filtry')} />
            <Tab value={1} label={t('Widoczność kolumn')} />
            <Tab value={2} label={t('Zapisane ustawienia')} />
          </Tabs>
          <IconButton
            aria-label={t('Zamknij')}
            onClick={onClose}
            className={styles.closeButton}
            size="small">
            <X size={28} />
          </IconButton>
        </div>

        <DialogContent
          className={styles.content}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
          <div className={styles.tabContent}>
            {tabIndex === 0 && (
              <Box className={styles.tabPanel}>
                <div className={styles.expressionsList}>
                  {filterRuleGroups.map((group, groupIndex) => (
                    <React.Fragment key={groupIndex}>
                      {groupIndex > 0 && <Divider sx={{ my: 2 }} />}
                      {group.map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                          <div className={styles.expressionRow}>
                            <div className={styles.cell}>
                              <WiseSelect<IWiseTableColumn>
                                styleGroup="portal"
                                mode="singlechoice"
                                placeholder={t('Kolumna')}
                                options={columnOptions}
                                value={
                                  rowIndex > 0 ? group[0].columnFieldSymbol : row.columnFieldSymbol
                                }
                                onChange={(item) =>
                                  updateRow(groupIndex, rowIndex, {
                                    columnFieldSymbol: item?.field_symbol ?? null
                                  })
                                }
                                clearable={rowIndex === 0}
                                disabled={rowIndex > 0}
                                optionFontSize="14px"
                              />
                            </div>
                            <div className={styles.cell}>
                              <WiseSelect<{ value: TWiseTableAdvancedFilterOperator }>
                                styleGroup="portal"
                                mode="singlechoice"
                                placeholder={t('Operator')}
                                options={operatorOptions}
                                value={row.operator}
                                onChange={(item) =>
                                  updateRow(groupIndex, rowIndex, {
                                    operator: item?.value ?? null
                                  })
                                }
                                clearable
                                optionFontSize="14px"
                              />
                            </div>
                            <div className={styles.cellFlex}>
                              <FilterValueCell
                                filter={getFilterByFieldSymbol(row.columnFieldSymbol)}
                                value={row.value}
                                onChange={(v) => updateRow(groupIndex, rowIndex, { value: v })}
                              />
                            </div>
                            <IconButton
                              className={styles.removeRowButton}
                              onClick={() => addConditionInGroup(groupIndex, rowIndex)}
                              aria-label={t('Dodaj warunek')}
                              size="small">
                              <Plus size={18} />
                            </IconButton>
                            <IconButton
                              className={styles.removeRowButton}
                              onClick={() => removeRow(groupIndex, rowIndex)}
                              disabled={group.length <= 1 && filterRuleGroups.length <= 1}
                              aria-label={t('Usuń regułę')}
                              size="small">
                              <Trash size={18} />
                            </IconButton>
                          </div>
                          {rowIndex < group.length - 1 && (
                            <div className={styles.connectorRow}>
                              <WiseSelect<{ value: 'and' | 'or' }>
                                styleGroup="portal"
                                mode="singlechoice"
                                placeholder={t('Oraz')}
                                options={connectorOptions}
                                value={row.connectorAfter ?? 'and'}
                                onChange={(item) =>
                                  updateRow(groupIndex, rowIndex, {
                                    connectorAfter: item?.value ?? 'and'
                                  })
                                }
                                optionFontSize="14px"
                              />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
                <div className={styles.actions}>
                  <ButtonMui variant="outlined" color="secondary" onClick={addRule}>
                    {t('Dodaj regułę')}
                  </ButtonMui>
                </div>
              </Box>
            )}

            {tabIndex === 1 && (
              <Box className={styles.tabPanel}>
                <List className={styles.columnVisibilityList} disablePadding>
                  {orderedColumnsForVisibility.map((col, index) => (
                    <ListItem
                      key={col.field_symbol}
                      className={styles.columnVisibilityRow}
                      disablePadding
                      onDragOver={handleColumnDragOver}
                      onDrop={(e) => handleColumnDrop(e, index)}>
                      <span
                        className={styles.dragHandle}
                        aria-hidden
                        draggable
                        onDragStart={(e) => handleColumnDragStart(e, col.field_symbol, index)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            (e.currentTarget as HTMLElement).focus();
                          }
                        }}
                        title={t('Przeciągnij, aby zmienić kolejność')}>
                        <GripVertical size={18} />
                      </span>
                      <span className={styles.columnLabel}>{col.label}</span>
                      <Switch
                        size="small"
                        checked={pendingColumnVisibility[col.field_symbol] !== false}
                        onChange={(_, checked) =>
                          handleColumnVisibilityToggle(col.field_symbol, checked)
                        }
                        color="secondary"
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {tabIndex === 2 && (
              <Box className={styles.tabPanel}>
                <Typography variant="body2" color="text.secondary" className={styles.savedSection}>
                  {t('Wybierz ustawienia')}
                </Typography>
                <div className={styles.savedPresetRow}>
                  <div className={styles.savedPresetSelect}>
                    <WiseSelect<ISavedSettingsOptionItem>
                      styleGroup="portal"
                      mode="singlechoice"
                      placeholder={t('Wybierz zapisany zestaw ustawień')}
                      options={savedSettingsOptionsForSelect}
                      value={pendingSelectedPresetId}
                      onChange={(item) => handleSelectSettingsPreset(item)}
                      clearable
                      optionFontSize="14px"
                    />
                  </div>
                </div>
                <div className={styles.savedPresetRow} style={{ marginTop: 8 }}>
                  <ButtonMui
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleClearSavedFilters}>
                    {t('Wyczyść zapisane ustawienia')}
                  </ButtonMui>
                </div>
                {savedFilters.length > 0 && (
                  <div className={styles.savedFiltersSummary}>
                    <Typography variant="caption" color="text.secondary">
                      {t('Bieżące filtry')}:{' '}
                      {savedFilters.map((f: IWiseTableAdvancedFilter, i: number) => (
                        <span key={i}>
                          {getColumnLabel(f.columnFieldSymbol)}{' '}
                          {t(ADVANCED_FILTER_OPERATOR_LABELS[f.operator])} &quot;{f.value}&quot;
                          {i < savedFilters.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </Typography>
                  </div>
                )}
              </Box>
            )}
          </div>
        </DialogContent>
        {tabIndex === 0 && (
          <DialogActions sx={{ flexShrink: 0, px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <ButtonMui variant="text" color="error" onClick={clearAllFilters}>
              {t('Wyczyść')}
            </ButtonMui>
            <Box sx={{ flex: 1 }} />
            <ButtonMui
              variant="outlined"
              color="secondary"
              onClick={applyFiltersToRedux}
              disabled={!hasAnyCompleteRow}>
              {t('Zastosuj')}
            </ButtonMui>
            <ButtonMui
              variant="contained"
              color="secondary"
              onClick={handleOpenSaveFiltersNameDialog}
              disabled={!hasAnyCompleteRow}>
              {t('Zapisz filtry')}
            </ButtonMui>
          </DialogActions>
        )}
        {tabIndex === 1 && (
          <DialogActions sx={{ flexShrink: 0, px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ flex: 1 }} />
            <ButtonMui
              variant="outlined"
              color="secondary"
              onClick={applyColumnVisibility}
              disabled={!hasPendingColumnVisibilityChange}>
              {t('Zastosuj')}
            </ButtonMui>
            <ButtonMui
              variant="contained"
              color="secondary"
              onClick={handleOpenSaveSettingsNameDialog}>
              {t('Zapisz')}
            </ButtonMui>
          </DialogActions>
        )}
        {tabIndex === 2 && (
          <DialogActions sx={{ flexShrink: 0, px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ flex: 1 }} />
            <ButtonMui
              variant="outlined"
              color="secondary"
              onClick={handleApplySettingsPreset}
              disabled={!pendingSelectedPresetId}>
              {t('Zastosuj')}
            </ButtonMui>
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={saveNameDialogType !== null}
        onClose={handleCloseSaveNameDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { p: 2 } }}>
        <DialogTitle>
          {saveNameDialogType === 'filters'
            ? t('Nazwa zestawu filtrów')
            : t('Nazwa zestawu ustawień')}
        </DialogTitle>
        <DialogContent>
          {pendingSelectedPresetId != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography component="span" variant="body2">
                {t('Nadpisać ustawienie?')}
              </Typography>
              <Switch
                checked={overwritePresetWhenSaving}
                onChange={(_, checked) => setOverwritePresetWhenSaving(checked)}
                color="secondary"
                size="small"
              />
            </Box>
          )}
          {(!pendingSelectedPresetId || !overwritePresetWhenSaving) && (
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t('Nazwa zestawu')}
              value={saveNameValue}
              onChange={(e) => setSaveNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleConfirmSaveName();
              }}
              sx={{ mt: pendingSelectedPresetId != null ? 0 : 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <ButtonMui color="secondary" onClick={handleCloseSaveNameDialog}>
            {t('Anuluj')}
          </ButtonMui>
          <ButtonMui
            variant="contained"
            color="secondary"
            onClick={handleConfirmSaveName}
            disabled={!overwritePresetWhenSaving && !saveNameValue.trim()}>
            {t('Zapisz')}
          </ButtonMui>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdvancedFiltersDialog;
